/// <amd-module name="Wit/WorkItemForm/Links/GitHub/GitHubRepositorySelection" />
import { useTfsProject } from "Tfs/Platform/TfsComponent";
import { IVssPageContext } from "VSS/Platform/Context";
import { useService } from "VSS/Platform/Layout";
import { ISettingsService, SettingsUserScope } from "VSS/Platform/Settings";
import { FormItem } from "VSSUI/FormItem";
import { ExternalGitRepo } from "Wit/Clients/Internal/Contracts/ExternalEvents";
import * as Resources from "Wit/WorkItemForm/Resources";
import * as React from "react";
import { GitHubArtifactDropdownWithInfiniteScrolling } from "./GitHubArtifactDropdownWithInfiniteScrolling";

interface IGitHubRepositorySelectionProps {
    pageContext: IVssPageContext;
    selectedRepository: ExternalGitRepo | undefined;
    setSelectedRepository: (repository: ExternalGitRepo | undefined) => void;
    setShowSetUrlManually: (value: boolean) => void;
    cacheGitHubArtifactsFFEnabled?: boolean;
}

const GitHubSettingsKey = "GitHubSettings";
const GitHubSettingsScope = SettingsUserScope.Me;

export const GitHubRepositorySelection: React.FC<IGitHubRepositorySelectionProps> = props => {
    const settingsService = useService<ISettingsService>("ISettingsService");
    const [isLastSelectedRepositoryLoading, setIsLastSelectedRepositoryLoading] = React.useState<boolean>(true);
    const projectId = useTfsProject()?.id ?? "";
    const LastSelectedRepositoryKey = `LastSelectedRepository/${projectId}`;

    React.useEffect(() => {
        const loadLastSelectedRepository = async () => {
            try {
                const result = await settingsService.getEntriesAsync(GitHubSettingsKey, GitHubSettingsScope);
                const lastSelectedRepository: ExternalGitRepo | undefined = result[LastSelectedRepositoryKey];
                if (lastSelectedRepository) {
                    props.setSelectedRepository(lastSelectedRepository);
                }
            } catch (e) {
                // No need to show it to the user as the only downside of this fail is that he will not have preselected last repository in the dropdown.
                console.warn(e);
            } finally {
                setIsLastSelectedRepositoryLoading(false);
            }
        };

        loadLastSelectedRepository();
    }, []);

    return (
        <FormItem label={Resources.GithubRepositoryLabel}>
            {isLastSelectedRepositoryLoading ? (
                <div className="shimmer shimmer-line" aria-label={Resources.GithubRepositoriesLoadingLastSelectedRepository}>
                    &nbsp;
                </div>
            ) : (
                <GitHubArtifactDropdownWithInfiniteScrolling<ExternalGitRepo>
                    pageContext={props.pageContext}
                    selectedArtifact={props.selectedRepository}
                    setSelectedArtifact={repository => {
                        props.setSelectedRepository(repository);

                        const newSetting = {
                            [`${GitHubSettingsKey}/${LastSelectedRepositoryKey}`]: repository ?? null
                        };
                        settingsService.setEntries(newSetting, GitHubSettingsScope).catch(error => {
                            // If we fail to save the setting, the user will just have to select repository again next time, so no need to show an error to the user.
                            console.warn(error);
                        });
                    }}
                    dropdownIconName="Dictionary"
                    dropdownIconRender={() => (
                        <span className="prefix fabric-icon">
                            <svg
                                className="github-repository-icon"
                                xmlns="http://www.w3.org/2000/svg"
                                xmlnsXlink="http://www.w3.org/1999/xlink"
                                viewBox="0 0 16 16"
                                fill="currentColor"
                            >
                                <path d="M2 2.5A2.5 2.5 0 0 1 4.5 0h8.75a.75.75 0 0 1 .75.75v12.5a.75.75 0 0 1-.75.75h-2.5a.75.75 0 0 1 0-1.5h1.75v-2h-8a1 1 0 0 0-.714 1.7.75.75 0 1 1-1.072 1.05A2.495 2.495 0 0 1 2 11.5Zm10.5-1h-8a1 1 0 0 0-1 1v6.708A2.486 2.486 0 0 1 4.5 9h8ZM5 12.25a.25.25 0 0 1 .25-.25h3.5a.25.25 0 0 1 .25.25v3.25a.25.25 0 0 1-.4.2l-1.45-1.087a.249.249 0 0 0-.3 0L5.4 15.7a.25.25 0 0 1-.4-.2Z" />
                            </svg>
                        </span>
                    )}
                    dropdownId="id"
                    dropdownText={["name"]}
                    dropdownSortSelectedItemByText
                    infiniteScrollingDataProviderId="ms.vss-work-web.github-connected-repositories-data-provider"
                    cacheArtifacts={props.cacheGitHubArtifactsFFEnabled}
                    setShowSetUrlManually={props.setShowSetUrlManually}
                    resources={{
                        noResultsMessage: Resources.GithubRepositoriesEmptyMessage,
                        loadingMessage: Resources.GithubRepositoriesLoadingMessage,
                        loadingFailedMessage: Resources.GithubRepositoriesLoadingFailedMessage,
                        entities: Resources.GithubRepositories
                    }}
                />
            )}
        </FormItem>
    );
};
